/* eslint-disable @typescript-eslint/no-explicit-any */
import { accountsLinks, accountsRoutes } from '@/backend/config/routes';
import { SystemRoles } from '@/backend/features/permissions';
import { EnvironmentConfig } from '@/backend/types/extraTypes';
import { ContactManagementConfigProviderPrivate } from '@/ui/providers/ContactManagementConfigProviderPrivate';
import { CredentialingProvider } from '@/ui/providers/CredentialingProvider';
import { useInboxBadge } from '@juvare/alert-ui/ui/features/inbox';
import { useAlertsRoutes } from '@juvare/alert-ui/ui/hooks';
import { AlertConfigProvider } from '@juvare/alert-ui/ui/providers';
import { DialogHelper } from '@juvare/common-web-ui/DialogHelper';
import { GlobalApp } from '@juvare/common-web-ui/GlobalApp';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { SnackbarHelper } from '@juvare/common-web-ui/SnackbarHelper';
import { formatDateCustom } from '@juvare/common-web-ui/utils';
import { allowAccessToAccounts } from '@juvare/contact-management/backend/features/permissions/shared';
import { AccountAvatar } from '@juvare/contact-management/ui/features/accounts';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { EditablePageData } from '@juvare/platform-sdk-react/backend/features/editable-pages/shared';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useUserClient } from '@juvare/platform-sdk-react/ui/hooks';
import { getUserState, platformSlice } from '@juvare/platform-sdk-react/ui/stores';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavigateFunction, Outlet, useLocation } from 'react-router-dom';
import { createUserAppRoutes } from './UserAppRoutes';
import { ConfigLoader } from './components/ConfigLoader';
import { AppContext } from './components/Context';
import { LoginCompletionApp } from './components/LoginCompletionApp';
import { getTexasWizardStep } from './features/alerts/utils/getTexasWizardStep';
import { getTexasWizardStepData } from './features/alerts/utils/getTexasWizardStepData';
import { FormConfigContextProvider } from './features/forms/providers/FormConfigContextProvider';
import { useIdleUserMonitor } from './hooks/UseIdleUserMonitor';
import { useRedirectAfterLogin } from './hooks/useRedirectAfterLogin';
import { useSettingsMenu } from './hooks/useSettingsMenu';
import { useSupportCenterMenu } from './hooks/useSupportCenterMenu';
import { useSwitchUserInfo } from './hooks/useSwitchUserInfo';
import { baseName, getGlobalHeaderEnvironment, pendo } from './utils/utils';

interface UserAppProps {
    config: EnvironmentConfig;
    pages: EditablePageData[];
}

export const UserApp: React.FC<UserAppProps> = (props) => {
    const userState = useSelector(getUserState);
    const setting = useSelector(getSettings);
    const { t } = useTranslation<'healthAlerts'>();

    if (userState) {
        return (
            <AlertConfigProvider
                features={setting.featureFlags.notifications}
                availableChannels={setting.availableChannels}
                notificationOptions={setting.notificationOptions as any}
                notificationRichTextMessageBody={setting.notificationRichTextMessageBody}
                stateCode={props.config.stateCode}
                onGetAdditionalWizardStep={
                    setting.featureFlags.notifications.additionalStepWizard && props.config.stateCode === 'TX'
                        ? (config) => getTexasWizardStep(config, t)
                        : undefined
                }
                renderAdditionalWizardStepDetails={
                    setting.featureFlags.notifications.additionalStepWizard && props.config.stateCode === 'TX'
                        ? (alertResponse) => getTexasWizardStepData(alertResponse, setting, t)
                        : undefined
                }
            >
                <ContactManagementConfigProviderPrivate>
                    <CredentialingProvider>
                        <FormConfigContextProvider>
                            <ConfigLoader>
                                {userState.loginValidationResult.incompleteSections.length > 0 ? (
                                    <LoginCompletionApp />
                                ) : (
                                    <Content {...props} />
                                )}
                            </ConfigLoader>
                        </FormConfigContextProvider>
                    </CredentialingProvider>
                </ContactManagementConfigProviderPrivate>
            </AlertConfigProvider>
        );
    } else {
        throw new Error('Unauthorized');
    }
};

export const Content: React.FC<UserAppProps> = ({ config, pages }) => {
    const userState = useUserState();
    const setting = useSelector(getSettings);
    const { t } = useTranslation<'healthAlerts'>();
    const dispatch = useDispatch();

    // TODO: Should be replaced with Common-Web-UI implementation, but a CUI defect is preventing it right now (CUI-416)
    useIdleUserMonitor();
    const userClient = useUserClient();
    const info = useSwitchUserInfo();
    const { supportCenterRoutes, supportCenterHeaderItem } = useSupportCenterMenu(pages);
    const { settingsRoutes, settingsHeaderItems } = useSettingsMenu();
    const alertsRoutes = useAlertsRoutes();
    const navigationContext = React.useContext(NavigationContext);
    const { initState } = React.useContext(AppContext);

    React.useEffect(() => {
        if (userState?.id) {
            pendo.initialize({
                visitor: {
                    id: userState.id,
                    role: userState.systemRole,
                },
                account: {
                    id: config.clientId,
                    environment: config.environment,
                    product: 'HAN',
                },
            });
        }
    }, [userState?.id, userState.systemRole, config.clientId, config.environment]);

    const onSearch = allowAccessToAccounts(userState)
        ? (navigate: NavigateFunction, search: string, closeSearch: () => void) => {
              const urlParams = new URLSearchParams();
              urlParams.set('filter', JSON.stringify({ search: search }));
              navigate({ pathname: accountsLinks.root, search: urlParams.toString() });
              closeSearch();
          }
        : undefined;

    const headerPrimaryItem = setting.enablePendoIntegration
        ? {
              icon: <NewReleasesIcon />,
              label: t('common.whatsNew'),
              'data-test': 'whats-new',
          }
        : undefined;

    if (userState) {
        return (
            <GlobalApp
                appTitle={<b>{setting.appTitle}</b>}
                environment={getGlobalHeaderEnvironment(config)}
                basename={baseName}
                routes={[...createUserAppRoutes(userState, alertsRoutes, t), ...supportCenterRoutes, ...settingsRoutes]}
                juvareHref={config.oktaUrl}
                oktaUrl={config.oktaUrl}
                appImage={setting.showClientLogo ? <img src={setting.clientLogo} height='60' alt='' /> : undefined}
                searchLabel={t('appHeader.globalSearchLabel')}
                onSearch={onSearch}
                headerPrimaryItem={headerPrimaryItem}
                headerItems={[...supportCenterHeaderItem, ...settingsHeaderItems]}
                profileAvatarSmall={<AccountAvatar size='small' />}
                profileAvatarLarge={<AccountAvatar size='large' />}
                profileName={userState.displayName}
                profileBadge={userState.incompleteAccountSections > 0 ? userState.incompleteAccountSections : undefined}
                profileRole={t(`roles.${userState.systemRole as SystemRoles}`)}
                profileNavigateTo={accountsRoutes.root + '/' + userState.id}
                logOutOnClick={() => {
                    navigationContext.navigate(() => {
                        userClient.logout().finally(() => {
                            initState();
                            dispatch(platformSlice.actions.clearUser());
                        });
                    });
                }}
                {...info}
                footerCopyright={t('footer.message', { copyrightYear: formatDateCustom(new Date(__BUILD_TIMESTAMP__), 'yyyy') })}
                footerLinks={[
                    { label: t('footer.privacyPolicy'), href: 'https://www.juvare.com/privacy-policy/' },
                    { label: t('footer.termsAndConditions'), href: 'https://www.juvare.com/terms-conditions/' },
                ]}
            >
                <UserAppOutlet />
            </GlobalApp>
        );
    } else {
        throw new Error('Unauthorized');
    }
};

const UserAppOutlet: React.FC = () => {
    useRedirectAfterLogin();
    useInboxBadge();
    const { pathname } = useLocation();
    React.useEffect(() => {
        const originalTitle = document.title;
        const titles = pathname.split('/')[1].replace(/\W+/g, ' ').split(' ');
        const titleString = titles.map((title) => title.substring(0, 1).toUpperCase() + title.substring(1)).join(' ');
        document.title = titleString.length ? titleString + ' - ' + originalTitle : originalTitle;
        return () => {
            document.title = originalTitle;
        };
    }, [pathname]);
    return (
        <>
            <DialogHelper />
            <SnackbarHelper />
            <Outlet />
        </>
    );
};
